import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { compareVersions } from './FocusInfoDialog';
import axios from 'axios';
import DialogFocusWebVersions from './version/DialogFocusWebVersions';
import version from '../../version.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn } from '@fortawesome/pro-duotone-svg-icons';
import { setLastRelease } from '../../store/slices/RootSlice';
import {
  logoBludataDarkModeUrl,
  logoBludataWhiteModeUrl,
} from '../../utility_objects/const/imageUrls';
import { getIsImpersonation } from '@focus.cloud/src/login/msal/MsalInstance';
import moment from 'moment';

export const getVersioneFocus = () => version.version;
export const currentYear = new Date().getFullYear();

interface Props {
  /** @deprecated Non più utilizzato dopo il ripristino del flusso versioni */
  disableUpdateClick?: boolean;
  hideVersion?: boolean;
  transparent?: boolean;
}

function CrmFooter(props: Props) {
  const { transparent, hideVersion } = props;
  const dispatch = useDispatch();
  const [showVersion, setShowVersion] = useState(false);
  const dialogVersionOpenedRef = useRef(false);
  const { loginData, themeMode } = useSelector(
    (state: RootState) => state.root,
  );
  const { LastRelease, CodiceUtente } = loginData;
  const { Versione } = useSelector((state: RootState) => state.root.loginData);

  const onVersionClick = () => {
    dialogVersionOpenedRef.current = true;
    setShowVersion(true);
  };

  const onVersionClose = useCallback(() => {
    const currentVersion = getVersioneFocus();
    // Segna come vista solo alla chiusura della dialog
    axios
      .post('/focusapi/utente/setrelease', {
        release: currentVersion,
      })
      .then(() => {
        // Aggiorna lo store per evitare che il useEffect riapra la dialog
        dispatch(setLastRelease(currentVersion));
      })
      .catch(() => {
        // Errore silenzioso: al prossimo login la dialog si ripresenterà
      });
    setShowVersion(false);
  }, []);

  useEffect(() => {
    const currentVersion = getVersioneFocus();
    if (!currentVersion || dialogVersionOpenedRef.current) return;

    // Non aprire prima che il login sia completato (init non ancora eseguita)
    if (!CodiceUtente) return;

    // Se LastRelease è vuota (utente nuovo / primo login) → mostra le release notes
    if (!LastRelease) {
      onVersionClick();
      return;
    }

    // Se la versione corrente è più recente di quella vista → mostra le release notes
    if (
      LastRelease !== currentVersion &&
      compareVersions(currentVersion, LastRelease)
    ) {
      onVersionClick();
    }
  }, [LastRelease, CodiceUtente]);

  const getChipValue = useCallback(() => {
    const text = import.meta.env.VITE_NX_VERSION;
    if (!getIsImpersonation()) {
      if (!text) return 'LOCAL';
      if (text === 'PRD') return '';
      return text;
    }
    if (text !== undefined) return `${text} - ASSISTENZA`;
    return 'ASSISTENZA';
  }, []);

  let logoToUse =
    themeMode === 'dark' ? logoBludataDarkModeUrl : logoBludataWhiteModeUrl;

  const showServerVersion =
    import.meta.env.VITE_NX_VERSION === 'DEV' ||
    import.meta.env.VITE_NX_VERSION === 'UAT' ||
    !import.meta.env.VITE_NX_VERSION;

  //@ts-expect-error - __BUILD_DATE__ is injected at build time in vite.config.mts
  const versionDate = __BUILD_DATE__ ? new Date(__BUILD_DATE__) : null;
  const momentDate = versionDate ? moment(versionDate) : null;
  const buildDateString = momentDate ? momentDate.format('DD/MM/YYYY') : '';

  return (
    <Paper
      component="footer"
      sx={{
        pl: 0.5,
        pr: 0.5,
        borderRadius: '0px',
        zIndex: 5,
        boxShadow:
          '2px 2px 5px 0 rgba(0, 0, 0, 0.12), 2px 2px 5px 0 rgba(0, 0, 0, 0.08)',
        position: transparent ? 'absolute' : undefined,
        bottom: 0,
        right: 0,
        left: 0,
        backgroundColor: transparent ? 'rgba(255, 255, 255, 0.8)' : undefined,
      }}
    >
      <Stack flexDirection="row" gap={2} alignItems="center">
        <Stack flexDirection="row" alignItems="center">
          {showVersion && <DialogFocusWebVersions onClose={onVersionClose} />}
          {!hideVersion && (
            <Button
              sx={{
                fontSize: '0.875rem',
              }}
              onClick={onVersionClick}
              color="inherit"
              variant="text"
              startIcon={
                <FontAwesomeIcon
                  style={{
                    fontSize: '16px',
                  }}
                  icon={faBullhorn}
                />
              }
            >
              Versione: {getVersioneFocus()}
            </Button>
          )}
          {getChipValue() && (
            <Chip
              label={getChipValue()}
              color="error"
              sx={{ p: 0, mr: 1 }}
              size="small"
            />
          )}
          {showServerVersion && (
            <Stack flexDirection="row" gap={1}>
              <Typography>Versione Server: {Versione}</Typography>
              {buildDateString && (
                <Typography>
                  Data pubblicazione FRONTEND: {buildDateString}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
        <Box flex={1} />
        <Typography fontWeight="600">© {currentYear} Bludata S.r.l.</Typography>
        <a href="https://www.bludata.com" target="_blank" rel="noreferrer">
          <Stack flexDirection="column" justifyContent="center">
            <img
              style={{
                height: '30px',
                width: '115px',
                cursor: 'pointer',
              }}
              alt="Bludata S.r.l."
              src={logoToUse}
            />
          </Stack>
        </a>
      </Stack>
    </Paper>
  );
}

export default CrmFooter;
